import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  Target,
  MessageCircle,
  Home,
  Sparkles,
  MoreHorizontal,
  Users,
  Trophy,
  ShoppingBag,
  User,
  Settings,
  Info,
  CreditCard,
  X,
  Star,
  LayoutGrid,
  Rocket,
  Compass,
  BookOpen,
  BarChart3,
  LogOut,
  LogIn,
  Medal,
  Layers,
  Sun,
  Moon,
  GraduationCap,
  DollarSign
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAuthToken, getPortalType, getUserData } from '@/utils/auth';
import { getAnonymousUserPreferences } from '@/utils/anonymousAuth';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import BudleeAIIcon from '@/components/BudleeAIIcon';
import { usePreferences } from '@/context/PreferencesContext';
import { useCompliance } from '@/context/ComplianceContext';
import { useUserProfile } from '@/context/UserProfileContext';
import { preloadStudentRoute } from '@/routes/studentRoutePreloaders';
import { mediaUrl } from '@/api/config';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const bottomNavItems = [
  { id: 'quests', label: 'Quests', icon: Target, color: 'text-orange-400' },
  { id: 'chat', label: 'Chat', icon: MessageCircle, color: 'text-green-400' },
  { id: 'learn', label: 'Learn', icon: Home, color: 'text-orange-500' },
  { id: 'curiosity', label: 'Curiosity', icon: Sparkles, color: 'text-purple-400' },
  { id: 'more', label: 'More', icon: MoreHorizontal, color: 'text-gray-500' },
];

const curiosityMenuItems = [
  { id: 'curiosity-chapters', label: 'Chapter Topics', icon: Layers, color: 'text-purple-400' },
  { id: 'curiosity-topics', label: 'Advanced Topics', icon: Rocket, color: 'text-purple-400' },
];

const moreMenuItems = [
  { id: 'tuitions', label: 'Tuitions', icon: GraduationCap, color: 'text-indigo-400' },
  { id: 'students-analytics', label: 'My Analytics', icon: BarChart3, color: 'text-emerald-400' },
  { id: 'leaderboard', label: 'Leaderboard', icon: Medal, color: 'text-yellow-400' },
  { id: 'founders', label: 'Student Founders', icon: Star, color: 'text-yellow-500' },
  { id: 'shop', label: 'Shop', icon: ShoppingBag, color: 'text-red-400' },
  { id: 'upgrade-plan', label: 'Upgrade Plan', icon: CreditCard, color: 'text-blue-400' },
  { id: 'profile', label: 'Profile', icon: User, color: 'text-gray-400' },
  { id: 'affiliate-earnings', label: 'Earnings', icon: DollarSign, color: 'text-emerald-400' },
  { id: 'settings', label: 'Settings', icon: Settings, color: 'text-gray-400' },
  { id: 'quick-guide', label: 'User Guide', icon: BookOpen, color: 'text-green-400' },
  { id: 'about', label: 'About', icon: Info, color: 'text-green-400' },
];

export default function MobileBottomNav({ activeSection, onSectionChange, onNavigate = () => { }, onLogout = () => { }, onUnlockFullAccess = () => { }, iconOnly = false, isDisabled = false }) {
  const navigate = useNavigate();
  const { show_subscriptions, hide_tuition } = useCompliance();
  const { theme, toggleTheme, config } = usePreferences();
  const { profile, subscription, inventory } = useUserProfile();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showCuriosityMenu, setShowCuriosityMenu] = useState(false);
  const portal = getPortalType(window.location.pathname || '');
  const authToken = getAuthToken(portal);
  const isGuest = !authToken && !!getAnonymousUserPreferences();

  const [userProfile, setUserProfile] = useState(null);
  const [equippedItems, setEquippedItems] = useState({
    CHARACTER: null,
    AVATAR: null,
    FRAME: null,
  });

  const isDark = theme === 'dark';
  const { t } = useTranslation(['nav', 'common']);

  const getNavLabel = (id, fallback) => {
    const keyMap = {
      'learn': 'learn',
      'chat': 'chat',
      'quests': 'quests',
      'tuitions': 'tuitions',
      'curiosity': 'curiosityCentre',
      'curiosity-chapters': 'chapterTopics',
      'curiosity-topics': 'advancedTopics',
      'achievements': 'achievements',
      'students-analytics': 'myAnalytics',
      'leaderboard': 'leaderboard',
      'founders': 'studentFounders',
      'shop': 'shop',
      'more': 'more',
      'profile': 'profile',
      'affiliate-earnings': 'earnings',
      'upgrade-plan': 'upgradePlan',
      'settings': 'settings',
      'quick-guide': 'userGuide',
      'about': 'about',
      'unlock-access': 'unlockAccess',
    };
    const key = keyMap[id];
    return key ? t(`nav:${key}`, fallback) : fallback;
  };

  // Map user profile from context to local UI format
  useEffect(() => {
    if (profile) {
      const photoField = profile.profile_photo || profile.profile_pic_url || profile.photo || profile.avatar;
      const photoUrl = photoField ? mediaUrl(photoField) : null;
      const isCustomCurriculum = Boolean(profile.is_custom_curriculum || profile.board?.is_custom);
      const gradeName = profile.student?.grade?.grade_name || profile.grade?.grade_name || null;
      const gradeNumber = profile.student?.grade?.grade_number || profile.grade?.grade_number || null;

      setUserProfile({
        photo: photoUrl,
        fullName: profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username || 'User',
        username: profile.username?.includes('__') ? profile.username.split('__')[1] : (profile.username || ''),
        isCustomCurriculum: isCustomCurriculum,
        syllabus: isCustomCurriculum ? null : (profile.board?.board_code || profile.board?.board_name || profile.syllabus || null),
        grade: isCustomCurriculum ? null : gradeName,
        gradeNumber: isCustomCurriculum ? null : gradeNumber,
        planName: subscription?.plan_name || (subscription?.status === 'FREE' || !subscription?.status ? 'Free' : null),
        isFounder: profile.is_founder || false,
      });
    }
  }, [profile, subscription]);

  // Map equipped items from context-provided inventory
  useEffect(() => {
    if (inventory && Array.isArray(inventory)) {
      const equipped = {
        CHARACTER: inventory.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'CHARACTER') || null,
        AVATAR: inventory.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'AVATAR') || null,
        FRAME: inventory.find(inv => inv.is_equipped && inv.shop_item?.item_type === 'FRAME') || null,
      };
      setEquippedItems(equipped);
    }
  }, [inventory]);

  const renderProfileAvatar = (size = 'normal') => {
    const isSmall = size === 'compact';
    const userData = getUserData(portal);
    const userType = userData?.user_type || portal;
    const useProfilePicAsAvatar = (userType === 'teacher' || userType === 'admin') || (userData?.use_profile_pic_as_avatar || false);
    const imageToShow = (userType === 'student' && !useProfilePicAsAvatar && equippedItems.AVATAR?.shop_item?.image_url_display)
      ? equippedItems.AVATAR.shop_item.image_url_display
      : (userProfile?.photo || null);
    const hasImage = !!imageToShow;

    return (
      <div className={cn(
        'rounded-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] flex items-center justify-center overflow-visible flex-shrink-0 relative',
        isSmall ? 'w-10 h-10' : 'w-10 h-10 sm:w-12 sm:h-12'
      )}>
        {/* Avatar/Profile Photo - Base Layer */}
        <div className="absolute inset-0 w-full h-full rounded-full overflow-hidden z-0">
          {hasImage ? (
            <img
              src={imageToShow}
              alt={userProfile?.fullName || 'User'}
              className="w-full h-full object-cover"
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : null}
          <div className={cn(
            "absolute inset-0 w-full h-full items-center justify-center",
            hasImage ? "hidden" : "flex"
          )}>
            <User className={cn("text-white", isSmall ? "w-5 h-5" : "w-6 h-6")} />
          </div>
        </div>

        {/* Profile Frame Overlay */}
        {equippedItems.FRAME?.shop_item?.image_url_display && (
          <div className={cn(
            "absolute rounded-full overflow-visible pointer-events-none z-10",
            isSmall ? "-inset-2.5" : "-inset-3"
          )}>
            <img
              src={equippedItems.FRAME.shop_item.image_url_display}
              alt="Frame"
              className="w-full h-full object-contain"
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>
        )}

        {/* Founder Badge */}
        {userProfile?.isFounder && (
          <div
            className={cn(
              "absolute -top-1 -right-1 z-20 bg-yellow-500 rounded-full border border-white dark:border-gray-800 shadow-md flex items-center justify-center",
              isSmall ? "p-0.5" : "p-1"
            )}
            title="Student Founder"
          >
            <Star className={cn("text-white fill-current", isSmall ? "w-2.5 h-2.5" : "w-2.5 h-2.5 sm:w-3 sm:h-3")} />
          </div>
        )}
      </div>
    );
  };

  const isCuriosityActive = activeSection === 'curiosity-chapters' || activeSection === 'curiosity-topics' || activeSection === 'curiosity';
  const isMoreActive = ['tuitions', 'students-analytics', 'leaderboard', 'founders', 'shop', 'upgrade-plan', 'profile', 'affiliate-earnings', 'settings', 'quick-guide', 'about'].includes(activeSection);

  // Filter more menu items - hide profile, settings, and upgrade-plan for guest users
  const isNative = Capacitor.isNativePlatform();

  // Filter more menu items - hide profile, settings, and upgrade-plan for guest users
  // Also hide upgrade-plan for native apps (Web-only billing pattern)
  const isAffiliator = Boolean(profile?.is_affiliator || getUserData(portal)?.is_affiliator);
  const visibleMoreMenuItems = (isGuest
    ? moreMenuItems
      .filter(item => {
        if (!show_subscriptions && item.id === 'shop') return false;
        return !['tuitions', 'profile', 'affiliate-earnings', 'students-analytics', 'upgrade-plan', 'logout'].includes(item.id);
      })
      .concat([{ id: 'unlock-access', label: 'Login / Signup', icon: LogIn, color: 'text-green-400' }])
    : moreMenuItems.filter(item => {
      if (item.id === 'affiliate-earnings') return show_subscriptions && isAffiliator;
      if (item.id === 'tuitions' && (hide_tuition || config?.budlee_tuitions_enabled !== true)) return false;
      if (!show_subscriptions && ['shop', 'upgrade-plan'].includes(item.id)) return false;
      if (profile?.institute && item.id === 'upgrade-plan') return false;
      return true;
    }));

  const visibleCuriosityMenuItems = curiosityMenuItems;

  const handleItemClick = (itemId) => {
    preloadStudentRoute(itemId)?.catch(() => { });

    if (itemId === 'more') {
      setShowMoreMenu(true);
      return;
    }

    if (itemId === 'curiosity') {
      setShowCuriosityMenu(true);
      return;
    }

    onSectionChange(itemId);
    setShowMoreMenu(false);
    setShowCuriosityMenu(false);
    onNavigate();
  };

  const handleSubItemClick = (itemId) => {
    preloadStudentRoute(itemId)?.catch(() => { });

    if (itemId === 'quick-guide') {
      window.dispatchEvent(new CustomEvent('showDetailedUserGuide', { detail: { force: true } }));
      setShowMoreMenu(false);
      onNavigate();
      return;
    }
    if (itemId === 'logout') {
      setShowMoreMenu(false);
      onLogout();
      return;
    }
    if (itemId === 'unlock-access') {
      setShowMoreMenu(false);
      onUnlockFullAccess();
      return;
    }
    onSectionChange(itemId);
    setShowMoreMenu(false);
    setShowCuriosityMenu(false);
    onNavigate();
  };

  // Reset scroll position when sheet opens
  const handleMenuOpen = (open, menuType) => {
    if (menuType === 'more') setShowMoreMenu(open);
    if (menuType === 'curiosity') setShowCuriosityMenu(open);

    if (open) {
      setTimeout(() => {
        const sheetContent = document.querySelector('[data-radix-dialog-content]');
        if (sheetContent) {
          sheetContent.scrollTop = 0;
        }
      }, 100);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/* Manual Backdrop for Bottom Sheets - Dimmed and Blurred background while keeping Bars interactive */}
      {(showMoreMenu || showCuriosityMenu) && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-md z-[10500] animate-in fade-in duration-300"
          aria-hidden="true"
          onClick={() => {
            setShowMoreMenu(false);
            setShowCuriosityMenu(false);
          }}
        />
      )}

      {/* Bottom Navigation Bar */}
      <div className={cn(
        'lg:hidden fixed bottom-0 left-0 right-0 bg-[hsl(var(--card-bg))]/95 backdrop-blur-md border-t border-[hsl(var(--card-border))] pb-safe w-full z-[12000] flex-shrink-0 transition-all duration-300',
        iconOnly ? 'h-[calc(3.25rem+env(safe-area-inset-bottom,0px))]' : 'h-[calc(4rem+env(safe-area-inset-bottom,0px))]',
        isDisabled && 'pointer-events-none opacity-50'
      )}>
        <div className={cn('flex items-center justify-between px-2', iconOnly ? 'h-[52px]' : 'h-16')}>
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            let isActive = activeSection === item.id;
            if (item.id === 'curiosity') isActive = isCuriosityActive;
            if (item.id === 'more') isActive = isMoreActive;

            return (
              <button
                key={item.id}
                onClick={(e) => {
                  e.preventDefault();
                  handleItemClick(item.id);
                }}
                onPointerDown={() => preloadStudentRoute(item.id)?.catch(() => { })}
                onPointerEnter={() => preloadStudentRoute(item.id)?.catch(() => { })}
                onFocus={() => preloadStudentRoute(item.id)?.catch(() => { })}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center p-1 rounded-lg transition-all duration-200 relative group touch-manipulation',
                  isActive
                    ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                    : 'text-[hsl(var(--muted-foreground))] active:text-[hsl(var(--foreground))] active:bg-[hsl(var(--muted))]/50'
                )}
              >
                <Icon className={cn(iconOnly ? 'w-6 h-6' : 'w-5 h-5 mb-0.5', item.color)} />
                {!iconOnly && (
                  <span className="text-[10px] font-medium transition-all duration-200 opacity-100">
                    {getNavLabel(item.id, item.label)}
                  </span>
                )}

                {/* Active indicator */}
                {isActive && (
                  <div className="absolute -top-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[hsl(var(--primary))] rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Curiosity Menu Sheet */}
      <Sheet open={showCuriosityMenu} onOpenChange={(open) => handleMenuOpen(open, 'curiosity')} modal={false}>
        <SheetContent
          side="bottom"
          hideClose
          className={cn(
            "rounded-t-xl max-h-[80vh] h-fit min-h-0 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] z-[11000] p-3.5 pb-2.5",
            iconOnly ? "bottom-[calc(3.25rem+env(safe-area-inset-bottom,0px))]" : "bottom-[calc(4rem+env(safe-area-inset-bottom,0px))]"
          )}
        >
          <SheetHeader className="text-start pb-3">
            <SheetTitle className="text-[hsl(var(--muted-foreground))] flex items-center gap-2 text-sm uppercase tracking-wider font-bold">
              <Sparkles className="w-5 h-5 text-purple-400" />
              {t('nav:curiosityCentre', 'Curiosity Centre')}
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-1 pb-1">
            {visibleCuriosityMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => handleSubItemClick(item.id)}
                  onPointerDown={() => preloadStudentRoute(item.id)?.catch(() => { })}
                  onFocus={() => preloadStudentRoute(item.id)?.catch(() => { })}
                  className={cn(
                    'w-full flex items-center gap-3.5 py-2.5 px-3 rounded-lg transition-all duration-200 text-start relative overflow-hidden touch-manipulation',
                    isActive
                      ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                      : 'text-[hsl(var(--muted-foreground))] active:bg-[hsl(var(--muted))]'
                  )}
                >
                  {isActive && (
                    <div className="absolute inset-0 rounded-lg p-[1px] bg-gradient-to-r from-[hsl(var(--teal-vivid))] to-[hsl(var(--green-bright))] opacity-20" />
                  )}
                  <Icon className={cn('w-5 h-5 flex-shrink-0', item.color)} />
                  <span className="font-medium text-sm">{getNavLabel(item.id, item.label)}</span>
                  {isActive && (
                    <div className="ms-auto w-2 h-2 bg-[hsl(var(--primary))] rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>


      {/* More Menu Sheet */}
      <Sheet open={showMoreMenu} onOpenChange={(open) => handleMenuOpen(open, 'more')} modal={false}>
        <SheetContent
          side="bottom"
          hideClose
          className={cn(
            "rounded-t-xl max-h-[85vh] h-fit min-h-0 overflow-y-auto bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] z-[11000] p-3.5 pb-2.5",
            iconOnly ? "bottom-[calc(3.25rem+env(safe-area-inset-bottom,0px))]" : "bottom-[calc(4rem+env(safe-area-inset-bottom,0px))]"
          )}
        >
          <SheetHeader className="text-start pb-3">
            <SheetTitle className="text-[hsl(var(--muted-foreground))] flex items-center gap-2 text-sm uppercase tracking-wider font-bold">
              <MoreHorizontal className="w-5 h-5" />
              {t('nav:moreOptions', 'More Options')}
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-1" role="menu" aria-label="More options menu">
            {visibleMoreMenuItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = false;

              return (
                <button
                  key={item.id}
                  onClick={() => handleSubItemClick(item.id)}
                  onPointerDown={() => preloadStudentRoute(item.id)?.catch(() => { })}
                  onFocus={() => preloadStudentRoute(item.id)?.catch(() => { })}
                  className={cn(
                    'w-full flex items-center gap-3.5 py-2.5 px-3 rounded-lg transition-all duration-200 text-start touch-manipulation',
                    isActive
                      ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20'
                      : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
                  )}
                  role="menuitem"
                >
                  <Icon className={cn('w-5 h-5 flex-shrink-0', item.color)} />
                  <span className="font-medium text-sm">{getNavLabel(item.id, item.label)}</span>
                </button>
              );
            })}
          </div>

          {/* User Profile / Theme / Logout Section (matching Sidebar bottom) */}
          {!isGuest && (
            <div className="mt-1">
              {userProfile ? (
                <div className="flex items-center justify-between gap-2.5 px-2.5 py-2 bg-[hsl(var(--sidebar-hover))]/50 rounded-2xl border border-[hsl(var(--card-border))] shadow-sm">
                  <button
                    onClick={() => {
                      navigate('/student/profile');
                      setShowMoreMenu(false);
                      onNavigate();
                    }}
                    className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity cursor-pointer text-start flex-1"
                    title="View Profile"
                  >
                    {renderProfileAvatar('normal')}
                    <div className="min-w-0 flex-1 flex flex-col items-start text-start">
                      <p className="text-xs font-bold text-foreground truncate capitalize w-full text-start leading-tight">
                        {userProfile.fullName}
                      </p>
                      {(userProfile.planName || (!userProfile.isCustomCurriculum && userProfile.grade)) && (
                        <p className="text-[10px] font-bold text-[#27C87C] truncate tracking-wider leading-tight mb-0.5" style={{ textShadow: '0 0 10px rgba(39, 200, 124, 0.2)' }}>
                          <span className="capitalize">{userProfile.planName?.toLowerCase()}</span> {!userProfile.isCustomCurriculum && userProfile.grade && <span className="mx-2 opacity-80">-</span>} {!userProfile.isCustomCurriculum && userProfile.grade && <span className="capitalize">{userProfile.grade?.toLowerCase()}</span>}
                        </p>
                      )}
                    </div>
                  </button>

                  <div className="flex items-center gap-0.5 shrink-0">
                    {/* Language Switcher */}
                    <LanguageSwitcher compact={true} />
                    {/* Theme Toggle Button */}
                    <button
                      onClick={toggleTheme}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors"
                      title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                      aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                    >
                      {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5" />}
                    </button>
                    {/* Logout Icon Button */}
                    <button
                      onClick={() => {
                        setShowMoreMenu(false);
                        onLogout();
                      }}
                      className="p-1.5 rounded-lg transition-all duration-300 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                      title="Logout"
                      aria-label="Logout"
                    >
                      <LogOut className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-end gap-1 px-2 py-1">
                  <LanguageSwitcher compact={true} />
                  <button
                    onClick={toggleTheme}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-hover))] transition-colors"
                    title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                    aria-label={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}
                  >
                    {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      onLogout();
                    }}
                    className="p-1.5 rounded-lg transition-all duration-300 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    title="Logout"
                    aria-label="Logout"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>,
    document.body
  );
}
